import { useEffect, useMemo, useState } from 'react'
import { listAllAdressen } from '../../helpers/pdok/fetchPdok'

// Alle adressen in de gekozen wijken van een wervingsactie, uit PDOK, met de
// stand van het laden.
//
// Nodig omdat AddressSelect dit zelf ophaalde, en die staat alleen op het
// tabblad 'Gebied': elk bezoek aan dat tabblad haalde de hele wijk opnieuw
// op (Zuidwijken: 71 pagina's na elkaar), en zolang dat liep stonden de
// groepsvinkjes uit. Nu één keer per actie, in Detail.jsx.
//
// 'complete' betekent écht compleet. Pagina's komen gestreamd binnen en gaan
// eens per animatieframe de lijst in; bij klaar of mislukt wordt de rest
// synchroon doorgeschoven, want anders kon 'complete' eerder komen dan de
// laatste adressen, en in een verborgen tab loopt dat frame helemaal niet.
// Een groepsvinkje dat op 'complete' wacht, kiest zo nooit een halve wijk.
//
// AI-CONTEXT: See docs/features/wervingsacties-registratie.md (Uitbreiding 2026-10)
// AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading

/**
 * @typedef {'idle' | 'loading' | 'complete' | 'failed'} LaadStand
 */

/**
 * @param {Array<{ WijkCode: string }>} wijken - De gekozen wijken van de actie
 * @returns {{ options: Array<object>, loadState: LaadStand }}
 */
const useWijkAdressen = (wijken) => {
    const [options, setOptions] = useState(/** @type {Array<object>} */ ([]))
    const [loadState, setLoadState] = useState(/** @type {LaadStand} */ ('idle'))

    // Een vaste sleutel, zodat een nieuwe array met dezelfde wijken (elke
    // Firestore-snapshot) niet opnieuw ophaalt.
    // AI-CONTEXT: See docs/pitfalls.md#state-useeffect-inline-filter
    const wijkKey = useMemo(() => (wijken || []).map((w) => w.WijkCode).sort().join('|'), [wijken])

    useEffect(() => {
        const codes = wijkKey ? wijkKey.split('|') : []
        setOptions([])
        if (codes.length === 0) {
            setLoadState('idle')
            return undefined
        }

        const controller = new AbortController()
        let cancelled = false
        const seen = new Set()
        setLoadState('loading')

        let buffer = []
        let raf = 0
        const flush = () => {
            if (raf) cancelAnimationFrame(raf)
            raf = 0
            if (cancelled || buffer.length === 0) return
            const batch = buffer
            buffer = []
            setOptions((prev) => [...prev, ...batch])
        }
        const append = (chunk) => {
            if (cancelled) return
            const fresh = chunk.filter((a) => !seen.has(a.Key))
            if (fresh.length === 0) return
            fresh.forEach((a) => seen.add(a.Key))
            buffer.push(...fresh)
            if (!raf) raf = requestAnimationFrame(flush)
        }

        Promise.all(codes.map((code) => listAllAdressen({ wijkcode: code }, controller.signal, append)))
            .then(() => {
                if (cancelled) return
                flush()
                setLoadState('complete')
            })
            .catch((err) => {
                if (cancelled || err.name === 'AbortError') return
                console.error('PDOK adres list failed', err)
                flush()
                setLoadState('failed')
            })

        return () => {
            cancelled = true
            controller.abort()
            if (raf) cancelAnimationFrame(raf)
        }
    }, [wijkKey])

    return { options, loadState }
}

export default useWijkAdressen
